import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    CITIES,
    broadcastEmail,
    deleteEmailTemplateOverride,
    fetchBroadcasts,
    fetchCohorts,
    fetchEmailActivity,
    fetchEmailStats,
    fetchEmailTemplate,
    fetchEmailTemplates,
    previewEmail,
    saveEmailTemplateOverride,
    sendCustomEmail,
} from "@/lib/api";
import {
    EVENT_META,
    Field,
    PageHeader,
    StatCell,
    fmtTime,
    prettyTpl,
} from "../_shared";

const SUBTABS = [
    { key: "activity", label: "Activity" },
    { key: "compose", label: "Compose" },
    { key: "broadcast", label: "Broadcast" },
    { key: "templates", label: "Templates" },
];

const COHORT_STATUSES = [
    "received",
    "shortlisted",
    "interview_scheduled",
    "selected",
    "rejected",
    "closed",
];

export default function EmailsTab() {
    const [sub, setSub] = useState("activity");

    return (
        <div data-testid="emails-tab">
            <PageHeader
                eyebrow="Outbound"
                title="Emails"
                description="Send custom one-offs, broadcast to cohorts, and watch every Resend event roll in live."
            />

            <div className="flex items-center gap-1 bg-white/[0.04] border border-line rounded-full p-1 mb-8 max-w-xl">
                {SUBTABS.map((s) => (
                    <button
                        key={s.key}
                        type="button"
                        data-testid={`emails-sub-${s.key}`}
                        onClick={() => setSub(s.key)}
                        className={`px-4 py-2 rounded-full font-display font-bold uppercase tracking-[0.15em] text-[0.65rem] transition-colors flex-1 ${
                            sub === s.key
                                ? "bg-[#f4f4f0] text-[#0a0a0a]"
                                : "text-ink-soft hover:text-ink"
                        }`}
                    >
                        {s.label}
                    </button>
                ))}
            </div>

            {sub === "activity" && <ActivityPanel />}
            {sub === "compose" && <ComposePanel />}
            {sub === "broadcast" && <BroadcastPanel />}
            {sub === "templates" && <TemplatesPanel />}
        </div>
    );
}

// --------------------------------------------------------------------------- //
// ACTIVITY                                                                     //
// --------------------------------------------------------------------------- //

function ActivityPanel() {
    const [stats, setStats] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [eventType, setEventType] = useState("");
    const [template, setTemplate] = useState("");
    const [q, setQ] = useState("");
    const [days, setDays] = useState(30);
    const [templateList, setTemplateList] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, r] = await Promise.all([
                fetchEmailStats(days),
                fetchEmailActivity({
                    event_type: eventType || undefined,
                    template: template || undefined,
                    q: q || undefined,
                    days,
                    limit: 300,
                }),
            ]);
            setStats(s);
            setItems(r.items || []);
        } catch (e) {
            toast(e?.response?.data?.detail || "Failed to load activity.");
        } finally {
            setLoading(false);
        }
    }, [days, eventType, template, q]);

    useEffect(() => {
        load();
    }, [load]);
    useEffect(() => {
        fetchEmailTemplates().then((t) => setTemplateList(t.items || []));
    }, []);

    return (
        <div>
            {/* Stats */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-px bg-line border border-line rounded-2xl overflow-hidden mb-8">
                    <StatCell label="Total" value={stats.total} accent />
                    <StatCell label="Sent" value={stats.by_event["email.sent"] || 0} />
                    <StatCell label="Delivered" value={stats.by_event["email.delivered"] || 0} />
                    <StatCell label="Opened" value={stats.by_event["email.opened"] || 0} />
                    <StatCell label="Clicked" value={stats.by_event["email.clicked"] || 0} />
                    <StatCell label="Bounced" value={stats.by_event["email.bounced"] || 0} />
                    <StatCell label="Delayed" value={stats.by_event["email.delivery_delayed"] || 0} />
                </div>
            )}

            {/* Filters */}
            <div className="grid lg:grid-cols-12 gap-3 mb-6">
                <div className="lg:col-span-4">
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by recipient or ref ID…"
                        data-testid="activity-search"
                        className="w-full bg-white/[0.04] border border-line focus:border-[#5fe3c9] rounded-full px-5 py-3 outline-none"
                    />
                </div>
                <div className="lg:col-span-3">
                    <select
                        value={template}
                        onChange={(e) => setTemplate(e.target.value)}
                        data-testid="activity-template"
                        className="w-full appearance-none bg-white/[0.04] border border-line rounded-full px-5 py-3 outline-none"
                    >
                        <option value="">All templates</option>
                        {templateList.map((t) => (
                            <option key={t.key} value={t.key} className="bg-[#0a0a0a]">
                                {t.label}
                            </option>
                        ))}
                        <option value="custom_admin" className="bg-[#0a0a0a]">
                            Custom (one-off)
                        </option>
                        <option value="broadcast_custom" className="bg-[#0a0a0a]">
                            Broadcast (custom)
                        </option>
                    </select>
                </div>
                <div className="lg:col-span-3">
                    <select
                        value={eventType}
                        onChange={(e) => setEventType(e.target.value)}
                        data-testid="activity-event-type"
                        className="w-full bg-white/[0.04] border border-line rounded-full px-5 py-3 outline-none"
                    >
                        <option value="">All events</option>
                        {Object.entries(EVENT_META).map(([k, v]) => (
                            <option key={k} value={k} className="bg-[#0a0a0a]">
                                {v.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="lg:col-span-2">
                    <select
                        value={String(days)}
                        onChange={(e) => setDays(Number(e.target.value))}
                        data-testid="activity-days"
                        className="w-full bg-white/[0.04] border border-line rounded-full px-5 py-3 outline-none"
                    >
                        <option value="7" className="bg-[#0a0a0a]">7 days</option>
                        <option value="30" className="bg-[#0a0a0a]">30 days</option>
                        <option value="90" className="bg-[#0a0a0a]">90 days</option>
                    </select>
                </div>
            </div>

            <div className="flex items-center justify-between mb-3">
                <p className="vc-section-label text-ink-soft">
                    {loading ? "Loading…" : `${items.length} events`}
                </p>
                <button
                    type="button"
                    onClick={load}
                    className="vc-btn vc-btn-ghost"
                    data-testid="activity-refresh"
                >
                    Refresh
                </button>
            </div>

            <div
                className="border border-line rounded-2xl overflow-hidden"
                data-testid="activity-feed"
            >
                <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-white/[0.04] border-b border-line vc-section-label text-ink-soft">
                    <div className="col-span-2">Event</div>
                    <div className="col-span-3">Recipient</div>
                    <div className="col-span-2">Ref</div>
                    <div className="col-span-3">Template</div>
                    <div className="col-span-2">When</div>
                </div>
                {items.length === 0 && !loading && (
                    <div className="px-5 py-10 text-ink-soft text-sm">
                        No matching events. If the Resend webhook secret isn't
                        wired yet, events won't show up here.
                    </div>
                )}
                {items.map((ev, i) => {
                    const meta =
                        EVENT_META[ev.event_type] || {
                            label: ev.event_type,
                            color: "#9d9d96",
                            dot: "#9d9d96",
                        };
                    return (
                        <div
                            key={`${ev.email_id}-${i}`}
                            className="grid grid-cols-1 md:grid-cols-12 gap-1 md:gap-4 px-5 py-3 border-b border-line last:border-0 items-center"
                            data-testid={`activity-row-${i}`}
                        >
                            <div className="md:col-span-2 flex items-center gap-2">
                                <span
                                    aria-hidden
                                    className="w-2 h-2 rounded-full"
                                    style={{ background: meta.dot }}
                                />
                                <span
                                    className="font-display font-bold uppercase tracking-[0.14em] text-xs"
                                    style={{ color: meta.color }}
                                >
                                    {meta.label}
                                </span>
                            </div>
                            <div className="md:col-span-3 text-sm text-ink truncate">
                                {ev.to || "—"}
                            </div>
                            <div className="md:col-span-2 font-mono text-sm text-ink-soft">
                                {ev.ref_id || "—"}
                            </div>
                            <div className="md:col-span-3 text-sm text-ink-soft truncate">
                                {ev.template ? prettyTpl(ev.template) : "—"}
                            </div>
                            <div className="md:col-span-2 text-xs text-ink-soft">
                                {fmtTime(ev.received_at)}
                            </div>
                            {ev.click_url && (
                                <a
                                    href={ev.click_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="md:col-span-12 text-xs text-ink hover:text-gradient break-all md:pl-0"
                                >
                                    ↗ {ev.click_url}
                                </a>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// --------------------------------------------------------------------------- //
// COMPOSE (one-off to a single applicant)                                      //
// --------------------------------------------------------------------------- //

function ComposePanel() {
    const [refId, setRefId] = useState("");
    const [subject, setSubject] = useState(
        "Quick note about your Vibecity application",
    );
    const [html, setHtml] = useState(DEFAULT_CUSTOM_HTML);
    const [busy, setBusy] = useState(false);
    const [previewHtml, setPreviewHtml] = useState("");

    const onPreview = async () => {
        try {
            const r = await previewEmail({ subject, html, data: {} });
            setPreviewHtml(r.html);
        } catch (e) {
            toast(e?.response?.data?.detail || "Preview failed.");
        }
    };

    const onSend = async () => {
        const id = refId.trim().toUpperCase();
        if (!id) {
            toast("Reference ID is required.");
            return;
        }
        if (!subject.trim() || !html.trim()) {
            toast("Subject and HTML are required.");
            return;
        }
        setBusy(true);
        try {
            await sendCustomEmail({
                reference_id: id,
                subject: subject.trim(),
                html,
            });
            toast(`Custom email queued to ${id}.`);
        } catch (e) {
            toast(e?.response?.data?.detail || "Send failed.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="grid lg:grid-cols-2 gap-6">
            <div className="space-y-4">
                <Field label="Recipient reference ID">
                    <input
                        value={refId}
                        onChange={(e) => setRefId(e.target.value)}
                        placeholder="VC-ABC123"
                        data-testid="compose-ref"
                        className="w-full bg-transparent border border-line rounded-lg px-3 py-2 outline-none focus:border-white/40 font-mono"
                    />
                </Field>
                <Field label="Subject">
                    <input
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        data-testid="compose-subject"
                        className="w-full bg-transparent border border-line rounded-lg px-3 py-2 outline-none focus:border-white/40"
                    />
                </Field>
                <Field label="HTML body — merge tags: {{first_name}} {{city}} {{reference_id}} {{full_name}}">
                    <textarea
                        value={html}
                        onChange={(e) => setHtml(e.target.value)}
                        rows={18}
                        data-testid="compose-html"
                        className="w-full bg-transparent border border-line rounded-lg px-3 py-2 outline-none focus:border-white/40 text-xs font-mono resize-none"
                    />
                </Field>
                <div className="flex gap-3">
                    <button
                        type="button"
                        onClick={onSend}
                        disabled={busy}
                        data-testid="compose-send"
                        className="vc-btn vc-btn-accent flex-1 justify-center disabled:opacity-60"
                    >
                        {busy ? "Sending…" : "Send email"}
                    </button>
                    <button
                        type="button"
                        onClick={onPreview}
                        className="vc-btn vc-btn-ghost"
                        data-testid="compose-preview"
                    >
                        Preview
                    </button>
                </div>
            </div>

            <PreviewPane html={previewHtml} placeholder="Hit Preview to render with sample data" />
        </div>
    );
}

const DEFAULT_CUSTOM_HTML = `<p>Hi {{first_name}},</p>
<p>Quick note about your application for Vibecity {{city}} — reference {{reference_id}}.</p>
<p>Write your message here in plain HTML. The system will inject merge tags automatically.</p>
<p>— Maddy<br/>Team Emergent</p>`;

// --------------------------------------------------------------------------- //
// BROADCAST                                                                    //
// --------------------------------------------------------------------------- //

function BroadcastPanel() {
    const [audience, setAudience] = useState("cohort"); // cohort | filters
    const [cohorts, setCohorts] = useState([]);
    const [cohortId, setCohortId] = useState("");
    const [cities, setCities] = useState([]);
    const [statuses, setStatuses] = useState([]);

    const [contentMode, setContentMode] = useState("template"); // template | custom
    const [templateList, setTemplateList] = useState([]);
    const [templateKey, setTemplateKey] = useState("under_review");
    const [customSubject, setCustomSubject] = useState(
        "Vibecity {{city}} — quick update",
    );
    const [customHtml, setCustomHtml] = useState(DEFAULT_CUSTOM_HTML);

    const [previewHtml, setPreviewHtml] = useState("");
    const [previewSubject, setPreviewSubject] = useState("");
    const [audienceCount, setAudienceCount] = useState(null);
    const [audienceSample, setAudienceSample] = useState([]);
    const [busy, setBusy] = useState(false);
    const [recentJobs, setRecentJobs] = useState([]);

    useEffect(() => {
        fetchCohorts().then(setCohorts).catch(() => {});
        fetchEmailTemplates().then((t) => setTemplateList(t.items || []));
        fetchBroadcasts(10)
            .then((r) => setRecentJobs(r.items || []))
            .catch(() => {});
    }, []);

    const refreshJobs = () =>
        fetchBroadcasts(10).then((r) => setRecentJobs(r.items || []));

    const audiencePayload = useMemo(() => {
        if (audience === "cohort") {
            return cohortId ? { cohort_id: cohortId } : null;
        }
        if (!cities.length && !statuses.length) return null;
        return { filters: { cities, statuses } };
    }, [audience, cohortId, cities, statuses]);

    const contentPayload = useMemo(() => {
        if (contentMode === "template") return { template_key: templateKey };
        return { subject: customSubject, html: customHtml };
    }, [contentMode, templateKey, customSubject, customHtml]);

    const onPreview = async () => {
        try {
            const r = await previewEmail({
                template_key: contentMode === "template" ? templateKey : undefined,
                subject: contentMode === "custom" ? customSubject : undefined,
                html: contentMode === "custom" ? customHtml : undefined,
                data: {},
            });
            setPreviewHtml(r.html);
            setPreviewSubject(r.subject);
        } catch (e) {
            toast(e?.response?.data?.detail || "Preview failed.");
        }
    };

    const onDryRun = async () => {
        if (!audiencePayload) {
            toast("Pick an audience first.");
            return;
        }
        setBusy(true);
        try {
            const r = await broadcastEmail({
                ...audiencePayload,
                ...contentPayload,
                dry_run: true,
            });
            setAudienceCount(r.count);
            setAudienceSample(r.sample || []);
            toast(`${r.count} recipient(s) match.`);
        } catch (e) {
            toast(e?.response?.data?.detail || "Dry run failed.");
        } finally {
            setBusy(false);
        }
    };

    const onSend = async () => {
        if (!audiencePayload) {
            toast("Pick an audience first.");
            return;
        }
        if (audienceCount == null) {
            toast("Run a Dry run first to confirm the audience.");
            return;
        }
        if (
            !window.confirm(
                `Send to ${audienceCount} recipient(s)? This can't be undone.`,
            )
        )
            return;
        setBusy(true);
        try {
            const r = await broadcastEmail({
                ...audiencePayload,
                ...contentPayload,
            });
            toast(`Broadcast queued (${r.queued} emails). Job ${r.job_id}.`);
            setAudienceCount(null);
            setAudienceSample([]);
            await refreshJobs();
        } catch (e) {
            toast(e?.response?.data?.detail || "Broadcast failed.");
        } finally {
            setBusy(false);
        }
    };

    const toggleCity = (c) =>
        setCities((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));
    const toggleStatus = (s) =>
        setStatuses((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));

    return (
        <div className="grid lg:grid-cols-2 gap-6">
            <div className="space-y-6">
                {/* Audience */}
                <section className="border border-line rounded-2xl p-5 space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="vc-section-label text-[#5fe3c9]">
                            1 · Audience
                        </div>
                        <div className="flex items-center gap-1 bg-white/[0.04] border border-line rounded-full p-1">
                            {["cohort", "filters"].map((m) => (
                                <button
                                    key={m}
                                    type="button"
                                    data-testid={`broadcast-audience-${m}`}
                                    onClick={() => setAudience(m)}
                                    className={`px-3 py-1 rounded-full text-[0.62rem] tracking-[0.18em] uppercase font-display font-bold ${
                                        audience === m
                                            ? "bg-[#f4f4f0] text-[#0a0a0a]"
                                            : "text-ink-soft"
                                    }`}
                                >
                                    {m}
                                </button>
                            ))}
                        </div>
                    </div>

                    {audience === "cohort" ? (
                        <Field label="Pick a cohort">
                            <select
                                value={cohortId}
                                onChange={(e) => setCohortId(e.target.value)}
                                data-testid="broadcast-cohort"
                                className="w-full bg-transparent border border-line rounded-lg px-3 py-2 outline-none focus:border-white/40"
                            >
                                <option value="">— Select cohort —</option>
                                {cohorts.map((c) => (
                                    <option
                                        key={c.id}
                                        value={c.id}
                                        className="bg-[#0a0a0a]"
                                    >
                                        {c.name} ({c.member_count})
                                    </option>
                                ))}
                            </select>
                        </Field>
                    ) : (
                        <div className="space-y-4">
                            <div>
                                <div className="text-[10px] tracking-[0.22em] uppercase text-ink-soft mb-2">
                                    Cities
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {CITIES.map((c) => (
                                        <button
                                            key={c}
                                            type="button"
                                            onClick={() => toggleCity(c)}
                                            data-testid={`broadcast-city-${c}`}
                                            className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.14em] border transition-colors ${cities.includes(c) ? "bg-ink text-background border-ink" : "border-line text-ink-soft hover:text-ink"}`}
                                        >
                                            {c}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] tracking-[0.22em] uppercase text-ink-soft mb-2">
                                    Statuses
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {COHORT_STATUSES.map((s) => (
                                        <button
                                            key={s}
                                            type="button"
                                            onClick={() => toggleStatus(s)}
                                            data-testid={`broadcast-status-${s}`}
                                            className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.14em] border transition-colors ${statuses.includes(s) ? "bg-ink text-background border-ink" : "border-line text-ink-soft hover:text-ink"}`}
                                        >
                                            {s.replace(/_/g, " ")}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={onDryRun}
                        disabled={busy}
                        data-testid="broadcast-dry-run"
                        className="vc-btn vc-btn-ghost"
                    >
                        {busy ? "Counting…" : "Dry-run audience"}
                    </button>
                    {audienceCount != null && (
                        <div className="text-sm text-ink">
                            <span className="text-[#5fe3c9] font-display font-black text-xl mr-2">
                                {audienceCount}
                            </span>
                            recipient(s) will receive this.
                            {audienceSample.length > 0 && (
                                <div className="mt-2 text-xs text-ink-soft space-y-1 max-h-24 overflow-y-auto">
                                    {audienceSample.slice(0, 5).map((s) => (
                                        <div
                                            key={s.reference_id}
                                            className="flex justify-between gap-3 truncate"
                                        >
                                            <span className="font-mono">
                                                {s.reference_id}
                                            </span>
                                            <span className="truncate">
                                                {s.full_name} · {s.city}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </section>

                {/* Content */}
                <section className="border border-line rounded-2xl p-5 space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="vc-section-label text-[#5fe3c9]">
                            2 · Content
                        </div>
                        <div className="flex items-center gap-1 bg-white/[0.04] border border-line rounded-full p-1">
                            {["template", "custom"].map((m) => (
                                <button
                                    key={m}
                                    type="button"
                                    data-testid={`broadcast-content-${m}`}
                                    onClick={() => setContentMode(m)}
                                    className={`px-3 py-1 rounded-full text-[0.62rem] tracking-[0.18em] uppercase font-display font-bold ${
                                        contentMode === m
                                            ? "bg-[#f4f4f0] text-[#0a0a0a]"
                                            : "text-ink-soft"
                                    }`}
                                >
                                    {m}
                                </button>
                            ))}
                        </div>
                    </div>

                    {contentMode === "template" ? (
                        <Field label="Template">
                            <select
                                value={templateKey}
                                onChange={(e) => setTemplateKey(e.target.value)}
                                data-testid="broadcast-template"
                                className="w-full bg-transparent border border-line rounded-lg px-3 py-2 outline-none focus:border-white/40"
                            >
                                {templateList.map((t) => (
                                    <option key={t.key} value={t.key} className="bg-[#0a0a0a]">
                                        {t.label}
                                    </option>
                                ))}
                            </select>
                        </Field>
                    ) : (
                        <>
                            <Field label="Subject — supports merge tags">
                                <input
                                    value={customSubject}
                                    onChange={(e) =>
                                        setCustomSubject(e.target.value)
                                    }
                                    data-testid="broadcast-subject"
                                    className="w-full bg-transparent border border-line rounded-lg px-3 py-2 outline-none focus:border-white/40"
                                />
                            </Field>
                            <Field label="HTML body — {{first_name}} {{city}} {{reference_id}}">
                                <textarea
                                    value={customHtml}
                                    onChange={(e) => setCustomHtml(e.target.value)}
                                    rows={12}
                                    data-testid="broadcast-html"
                                    className="w-full bg-transparent border border-line rounded-lg px-3 py-2 outline-none focus:border-white/40 text-xs font-mono resize-none"
                                />
                            </Field>
                        </>
                    )}
                </section>

                <div className="flex gap-3">
                    <button
                        type="button"
                        onClick={onSend}
                        disabled={busy || audienceCount == null}
                        data-testid="broadcast-send"
                        className="vc-btn vc-btn-accent flex-1 justify-center disabled:opacity-50"
                        title={
                            audienceCount == null
                                ? "Run Dry-run first"
                                : `Send to ${audienceCount}`
                        }
                    >
                        {busy ? "Queueing…" : `Send broadcast${audienceCount != null ? ` (${audienceCount})` : ""}`}
                    </button>
                    <button
                        type="button"
                        onClick={onPreview}
                        className="vc-btn vc-btn-ghost"
                        data-testid="broadcast-preview"
                    >
                        Preview
                    </button>
                </div>

                {/* Recent broadcasts */}
                {recentJobs.length > 0 && (
                    <section className="border border-line rounded-2xl p-5">
                        <div className="vc-section-label text-ink-soft mb-3">
                            Recent broadcasts
                        </div>
                        <div className="space-y-2 text-xs">
                            {recentJobs.map((j) => (
                                <div
                                    key={j.job_id}
                                    className="flex items-center justify-between gap-3"
                                    data-testid={`broadcast-job-${j.job_id}`}
                                >
                                    <div className="min-w-0">
                                        <div className="font-mono text-ink-soft truncate">
                                            {j.job_id} · {j.audience_label}
                                        </div>
                                        <div className="text-ink truncate">
                                            {j.subject}
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="font-display font-black text-sm">
                                            {j.sent ?? j.recipient_count}
                                            {j.failed ? (
                                                <span className="text-[#ff6b6b] ml-1">
                                                    /{j.failed}fail
                                                </span>
                                            ) : null}
                                        </div>
                                        <div className="text-ink-soft text-[10px]">
                                            {fmtTime(
                                                j.completed_at || j.created_at,
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
            </div>

            <PreviewPane
                subject={previewSubject}
                html={previewHtml}
                placeholder="Hit Preview to render with sample data"
            />
        </div>
    );
}

// --------------------------------------------------------------------------- //
// TEMPLATES (edit master overrides for lifecycle templates)                    //
// --------------------------------------------------------------------------- //

function TemplatesPanel() {
    const [items, setItems] = useState([]);
    const [activeKey, setActiveKey] = useState(null);
    const [detail, setDetail] = useState(null);
    const [subject, setSubject] = useState("");
    const [html, setHtml] = useState("");
    const [enabled, setEnabled] = useState(true);
    const [busy, setBusy] = useState(false);
    const [previewHtml, setPreviewHtml] = useState("");
    const [previewSubject, setPreviewSubject] = useState("");
    const [dirty, setDirty] = useState(false);

    const refreshList = useCallback(async () => {
        const r = await fetchEmailTemplates();
        setItems(r.items || []);
        return r.items || [];
    }, []);

    useEffect(() => {
        refreshList().then((arr) => {
            if (arr[0] && !activeKey) setActiveKey(arr[0].key);
        });
        // eslint-disable-next-line
    }, []);

    // Load the chosen template + its override (if any).
    useEffect(() => {
        if (!activeKey) return;
        let alive = true;
        fetchEmailTemplate(activeKey).then((d) => {
            if (!alive) return;
            setDetail(d);
            const ov = d.override;
            setSubject(ov?.subject || d.default_subject);
            setHtml(ov?.html || d.default_html);
            setEnabled(ov?.enabled ?? true);
            setPreviewHtml(d.default_html);
            setPreviewSubject(d.default_subject);
            setDirty(false);
        });
        return () => {
            alive = false;
        };
    }, [activeKey]);

    const onPreview = async () => {
        try {
            const r = await previewEmail({ subject, html, data: {} });
            setPreviewHtml(r.html);
            setPreviewSubject(r.subject);
        } catch (e) {
            toast(e?.response?.data?.detail || "Preview failed.");
        }
    };

    const onSave = async () => {
        if (!subject.trim() || !html.trim()) {
            toast("Subject and HTML are required.");
            return;
        }
        setBusy(true);
        try {
            await saveEmailTemplateOverride(activeKey, {
                subject: subject.trim(),
                html,
                enabled,
            });
            toast("Template override saved. Live on next send.");
            setDirty(false);
            await refreshList();
            const fresh = await fetchEmailTemplate(activeKey);
            setDetail(fresh);
        } catch (e) {
            toast(e?.response?.data?.detail || "Save failed.");
        } finally {
            setBusy(false);
        }
    };

    const onResetToDefault = async () => {
        if (
            !window.confirm(
                "Reset this template back to its built-in default? Any custom override will be deleted.",
            )
        )
            return;
        setBusy(true);
        try {
            await deleteEmailTemplateOverride(activeKey);
            toast("Reverted to default. The built-in template will be used.");
            await refreshList();
            const fresh = await fetchEmailTemplate(activeKey);
            setDetail(fresh);
            setSubject(fresh.default_subject);
            setHtml(fresh.default_html);
            setEnabled(true);
            setDirty(false);
        } catch (e) {
            toast(e?.response?.data?.detail || "Reset failed.");
        } finally {
            setBusy(false);
        }
    };

    const mark = (setter) => (e) => {
        setter(e.target.value);
        setDirty(true);
    };

    const insertMergeTag = (tag) => {
        // Append tag to subject if cursor is in subject, else html
        setHtml((h) => h + `{{${tag}}}`);
        setDirty(true);
    };

    return (
        <div className="grid lg:grid-cols-12 gap-6" data-testid="templates-panel">
            <div className="lg:col-span-3 space-y-2">
                <div className="vc-section-label text-ink-soft mb-2">
                    Lifecycle templates
                </div>
                {items.map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => {
                            if (
                                dirty &&
                                !window.confirm("Discard unsaved changes?")
                            )
                                return;
                            setActiveKey(t.key);
                        }}
                        data-testid={`template-${t.key}`}
                        className={`w-full text-left p-4 border rounded-xl transition-colors ${
                            activeKey === t.key
                                ? "border-[#5fe3c9] bg-[#5fe3c9]/5"
                                : "border-line hover:border-ink"
                        }`}
                    >
                        <div className="flex items-start justify-between gap-2">
                            <div className="font-display font-bold">{t.label}</div>
                            {t.has_master_override && (
                                <span
                                    className="text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 rounded-full bg-[#90FDA6]/15 text-[#90FDA6] border border-[#90FDA6]/30"
                                    data-testid={`template-${t.key}-overridden`}
                                >
                                    Custom
                                </span>
                            )}
                        </div>
                        <div className="text-[11px] text-ink-soft mt-1">
                            Fires on: {t.fires_on}
                        </div>
                    </button>
                ))}
                <p className="text-[11px] text-ink-soft pt-3 leading-relaxed">
                    Master overrides apply to every send unless a cohort
                    override for the same template wins.
                </p>
            </div>

            <div className="lg:col-span-9 space-y-4">
                {!detail && (
                    <div className="text-ink-soft text-sm">Loading template…</div>
                )}
                {detail && (
                    <>
                        <div className="flex items-center justify-between flex-wrap gap-3">
                            <div>
                                <div className="vc-section-label text-[#5fe3c9]">
                                    Editing
                                </div>
                                <h3
                                    className="font-display font-black uppercase tracking-tightest"
                                    style={{
                                        fontSize: "clamp(1.4rem, 2.2vw, 2rem)",
                                    }}
                                >
                                    {detail.label}
                                </h3>
                                <div className="text-xs text-ink-soft mt-1">
                                    Fires on:{" "}
                                    <span className="text-ink">
                                        {detail.fires_on}
                                    </span>
                                </div>
                            </div>
                            <label className="flex items-center gap-2 text-xs text-ink-soft">
                                <input
                                    type="checkbox"
                                    checked={enabled}
                                    onChange={(e) => {
                                        setEnabled(e.target.checked);
                                        setDirty(true);
                                    }}
                                    data-testid="template-enabled-toggle"
                                    className="accent-[#5fe3c9]"
                                />
                                <span className="uppercase tracking-[0.18em]">
                                    Active
                                </span>
                            </label>
                        </div>

                        <Field label="Subject — supports {{first_name}} {{city}} {{reference_id}} {{full_name}} {{status_url}}">
                            <input
                                value={subject}
                                onChange={mark(setSubject)}
                                data-testid="template-subject"
                                className="w-full bg-transparent border border-line rounded-lg px-3 py-2 outline-none focus:border-white/40"
                            />
                        </Field>

                        <div className="flex flex-wrap gap-2 text-[10px] tracking-[0.18em] uppercase text-ink-soft">
                            <span>Insert tag →</span>
                            {[
                                "first_name",
                                "full_name",
                                "city",
                                "reference_id",
                                "status_url",
                                "interview_at",
                            ].map((t) => (
                                <button
                                    key={t}
                                    type="button"
                                    onClick={() => insertMergeTag(t)}
                                    data-testid={`template-merge-${t}`}
                                    className="px-2 py-1 rounded-full border border-line hover:border-ink hover:text-ink transition-colors"
                                >
                                    {`{{${t}}}`}
                                </button>
                            ))}
                        </div>

                        <Field label="HTML body">
                            <textarea
                                value={html}
                                onChange={mark(setHtml)}
                                rows={16}
                                data-testid="template-html"
                                className="w-full bg-transparent border border-line rounded-lg px-3 py-2 outline-none focus:border-white/40 text-xs font-mono resize-none"
                            />
                        </Field>

                        <div className="flex flex-wrap items-center gap-3">
                            <button
                                type="button"
                                onClick={onSave}
                                disabled={busy || !dirty}
                                data-testid="template-save"
                                className="vc-btn vc-btn-accent disabled:opacity-50"
                            >
                                {busy ? "Saving…" : dirty ? "Save changes" : "Saved"}
                            </button>
                            <button
                                type="button"
                                onClick={onPreview}
                                className="vc-btn vc-btn-ghost"
                                data-testid="template-preview"
                            >
                                Preview
                            </button>
                            {detail.override && (
                                <button
                                    type="button"
                                    onClick={onResetToDefault}
                                    disabled={busy}
                                    data-testid="template-reset"
                                    className="vc-btn vc-btn-ghost text-[#ff6b6b]"
                                >
                                    Reset to default
                                </button>
                            )}
                            {dirty && (
                                <span className="text-[10px] text-[#F8FF28] uppercase tracking-[0.2em]">
                                    Unsaved changes
                                </span>
                            )}
                        </div>

                        <PreviewPane
                            subject={previewSubject}
                            html={previewHtml}
                            placeholder="Hit Preview to render with sample data"
                        />
                    </>
                )}
            </div>
        </div>
    );
}

// --------------------------------------------------------------------------- //
// Reusable preview pane (renders HTML inside a sandboxed iframe)               //
// --------------------------------------------------------------------------- //

function PreviewPane({ subject, html, placeholder }) {
    return (
        <div
            className="border border-line rounded-2xl overflow-hidden bg-white flex flex-col"
            style={{ minHeight: 520 }}
            data-testid="preview-pane"
        >
            <div className="px-4 py-3 bg-[#f0f0ec] border-b border-line">
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#777]">
                    Preview
                </div>
                <div className="font-display font-bold text-[#111] text-sm truncate">
                    {subject || "—"}
                </div>
            </div>
            {html ? (
                <iframe
                    title="email-preview"
                    srcDoc={html}
                    className="w-full flex-1 bg-white border-0"
                    sandbox=""
                    style={{ minHeight: 480 }}
                />
            ) : (
                <div className="flex-1 grid place-items-center text-sm text-[#777] p-10 text-center">
                    {placeholder}
                </div>
            )}
        </div>
    );
}
